import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DatePicker } from "antd";
import { ReactSVG } from "react-svg";
import { XMarkIcon } from "@heroicons/react/24/outline";
import DateTimePopup from "./DateTimePopup";

dayjs.extend(customParseFormat);

// Shared Calendar Icon Component
const CalendarIcon = ({ className }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M2 2.68182H22C22.8284 2.68182 23.5 3.35339 23.5 4.18182V9.31818H0.5V4.18182C0.5 3.35339 1.17157 2.68182 2 2.68182Z"
      fill="#0B4C5B"
      stroke="#0B4C5B"
    />
    <rect
      x="2.68176"
      y="0.5"
      width="1.18182"
      height="3.36364"
      rx="0.590909"
      fill="#0B4C5B"
      stroke="#0B4C5B"
    />
    <rect
      x="20.1364"
      y="0.5"
      width="1.18182"
      height="3.36364"
      rx="0.590909"
      fill="#0B4C5B"
      stroke="#0B4C5B"
    />
    <mask id="path-4-inside-1_115_240" fill="white">
      <path d="M0 10.9091H24V22C24 23.1046 23.1046 24 22 24H2C0.89543 24 0 23.1046 0 22V10.9091Z" />
    </mask>
    <path
      d="M0 10.9091H24H0ZM25 22C25 23.6569 23.6569 25 22 25H2C0.343146 25 -1 23.6569 -1 22H1C1 22.5523 1.44772 23 2 23H22C22.5523 23 23 22.5523 23 22H25ZM2 25C0.343146 25 -1 23.6569 -1 22V10.9091H1V22C1 22.5523 1.44772 23 2 23V25ZM25 10.9091V22C25 23.6569 23.6569 25 22 25V23C22.5523 23 23 22.5523 23 22V10.9091H25Z"
      fill="#0B4C5B"
      mask="url(#path-4-inside-1_115_240)"
    />
    <rect
      x="3.27271"
      y="12"
      width="4.36364"
      height="3.27273"
      rx="1"
      fill="#0B4C5B"
    />
    <rect
      x="3.27271"
      y="17.4545"
      width="4.36364"
      height="3.27273"
      rx="1"
      fill="#0B4C5B"
    />
    <rect
      x="16.3636"
      y="12"
      width="4.36364"
      height="3.27273"
      rx="1"
      fill="#0B4C5B"
    />
    <rect
      x="16.3636"
      y="17.4545"
      width="4.36364"
      height="3.27273"
      rx="1"
      fill="#0B4C5B"
    />
    <rect
      x="9.81824"
      y="12"
      width="4.36364"
      height="3.27273"
      rx="1"
      fill="#0B4C5B"
    />
    <rect
      x="9.81824"
      y="17.4545"
      width="4.36364"
      height="3.27273"
      rx="1"
      fill="#0B4C5B"
    />
  </svg>
);

// Shared suffix icon component
const DatePickerSuffixIcon = ({ clearableDatePicker, value, onClear, clearMessage, onCalendarClick }) => (
  <div className="flex items-center gap-2 pointer-events-auto">
    {clearableDatePicker && !!value && (
      <>
        <ReactSVG
          src="/icons/input-cross.svg"
          className="w-4 h-4 cursor-pointer hover:text-gray-700 py-1"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear(null);
          }}
        />
        <div className="h-5 w-px mr-2 bg-gray-200"></div>
      </>
    )}
    <div 
      className="calendar-icon-wrapper cursor-pointer"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onCalendarClick) {
          onCalendarClick(e);
        }
      }}
    >
      <CalendarIcon className="h-6 w-6" />
    </div>
  </div>
);

// Date-only picker (YYYY-MM-DD)
const CommonDatePicker = ({
  onChange,
  name,
  id,
  value,
  onFocus,
  inputClass,
  disabled = false,
  clearableDatePicker,
  disableFuture = false,
}) => {
  const [open, setOpen] = useState(false);
  const iconClickRef = useRef(false);

  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      iconClickRef.current = true;
      setOpen(!open);
    }
  };

  const handleOpenChange = (openState) => {
    if (openState && !iconClickRef.current) return;
    setOpen(openState);
    if (!openState) iconClickRef.current = false;
  };

  const resetAndClose = () => {
    setOpen(false);
    iconClickRef.current = false;
  };

  useEffect(() => {
    const inputElement = document.querySelector(`#${id}`);
    if (!inputElement) return;
    
    const handleInputClick = (e) => {
      if (!iconClickRef.current) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    };
    
    inputElement.addEventListener('click', handleInputClick, true);
    return () => inputElement.removeEventListener('click', handleInputClick, true);
  }, [id]);

  // Validate and parse date value (year must be > 1900)
  const getValidDateValue = () => {
    if (!value || (typeof value === 'string' && !value.trim())) return null;
    const dateObj = dayjs(String(value).trim(), "YYYY-MM-DD", true);
    return dateObj.isValid() && dateObj.year() > 1900 ? dateObj : null;
  };

  // Handle date change from DatePicker
  const handleDateChange = (dateObj) => {
    if (!dateObj?.isValid() || dateObj.year() <= 1900) {
      onChange(null);
    } else {
      onChange(dateObj.format("YYYY-MM-DD"));
    }
    resetAndClose();
  };

  const getPopupContainer = () => document.body;

  return (
    <div className="relative w-full overflow-visible">
      <DatePicker
        name={name}
        id={id}
        onFocus={onFocus}
        value={getValidDateValue()}
        defaultValue={null}
        defaultPickerValue={dayjs()}
        className={inputClass}
        onChange={handleDateChange}
        format="YYYY-MM-DD"
        allowClear={false}
        placeholder="YYYY-MM-DD"
        open={open}
        onOpenChange={handleOpenChange}
        inputReadOnly={true}
        suffixIcon={
          <DatePickerSuffixIcon
            clearableDatePicker={clearableDatePicker}
            value={value}
            onClear={(val) => {
              onChange(val);
              resetAndClose();
            }}
            clearMessage="Clear button clicked, clearing date"
            onCalendarClick={handleCalendarClick}
          />
        }
        bordered={false}
        disabled={disabled}
        disabledDate={disableFuture ? (current) => current && current.isAfter(dayjs(), 'day') : undefined}
        getPopupContainer={getPopupContainer}
        placement="bottomRight"
      />
    </div>
  );
};

// Date and time picker (YYYY-MM-DD HH:mm with display format YYYY-MM-DD | hh:mm A)
const CommonDateTimePicker = ({
  onChange,
  name,
  id,
  value,
  onFocus,
  inputClass,
  disabled = false,
  clearableDatePicker,
}) => {
  const dateTimeFormat = "YYYY-MM-DD HH:mm"; // Storage format (24-hour)
  const displayFormat = "YYYY-MM-DD | hh:mm A"; // Display format (12-hour with AM/PM)
  const parseFormat = "YYYY-MM-DD HH:mm";

  const parseValue = (val) => {
    if (!val) return null;
    if (typeof val === 'string') {
      const cleaned = val.replace(/\s*\|\s*/, ' ');
      const parsed = dayjs(cleaned, parseFormat);
      return parsed.isValid() ? parsed.toDate() : null;
    }
    const date = new Date(val);
    return isNaN(date.getTime()) ? null : date;
  };

  const [selectedDate, setSelectedDate] = useState(parseValue(value));
  const [selectedTime, setSelectedTime] = useState(
    parseValue(value) || new Date()
  );
  const [expanded, setExpanded] = useState(false);
  const wrapperRef = useRef();

  useEffect(() => {
    const parsed = parseValue(value);
    if (parsed) {
      setSelectedDate(parsed);
      setSelectedTime(parsed);
    }
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!wrapperRef.current) return;
      const clickedInsideInput = wrapperRef.current.contains(e.target);
      const clickedInsidePopup =
        typeof e.target?.closest === "function" &&
        !!e.target.closest(".hc-datetime-popup");

      if (!clickedInsideInput && !clickedInsidePopup) {
        setExpanded(false);
      }
    };
    if (expanded) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [expanded]);

  const formatDisplayValue = (date) => {
    if (!date) return { date: "YYYY-MM-DD", time: "00:00 AM" };
    
    const dateStr = date.toLocaleDateString('en-CA');
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    return { date: dateStr, time: timeStr };
  };

  const displayValue = formatDisplayValue(
    selectedDate && selectedTime
      ? new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          selectedTime.getHours(),
          selectedTime.getMinutes()
        )
      : null
  );

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
  };

  const handleBook = () => {
    if (!selectedDate || !selectedTime) return;
    const finalDateTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      selectedTime.getHours(),
      selectedTime.getMinutes()
    );
    const year = finalDateTime.getFullYear();
    const month = (finalDateTime.getMonth() + 1).toString().padStart(2, '0');
    const day = finalDateTime.getDate().toString().padStart(2, '0');
    const hours = finalDateTime.getHours().toString().padStart(2, '0');
    const minutes = finalDateTime.getMinutes().toString().padStart(2, '0');

    const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    onChange(formattedDateTime);
    setExpanded(false);
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDate(null);
    setSelectedTime(new Date());
    onChange(null);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setExpanded(true);
    }
  };

  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setExpanded(true);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full overflow-visible">
      <div
        className={`w-full border border-gray-300 rounded-md py-2 px-3 cursor-pointer bg-white transition-colors ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-teal-500"
        } ${inputClass || ""}`}
        onClick={handleInputClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-gray-700 text-sm">
            <span>{displayValue.date}</span>
            <span className="text-gray-400">|</span>
            <span>{displayValue.time}</span>
          </div>
          <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
            {clearableDatePicker && !!value && (
              <>
                <XMarkIcon
                  className="h-4 w-4 text-teal-950 hover:text-gray-600 cursor-pointer transition-colors"
                  onClick={handleClear}
                />
                <span className="text-gray-300">|</span>
              </>
            )}
            <div onClick={handleCalendarClick}>
              <CalendarIcon className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>
      <DateTimePopup
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        onDateSelect={handleDateSelect}
        onTimeChange={handleTimeChange}
        onBook={handleBook}
        wrapperRef={wrapperRef}
        open={expanded}
      />
    </div>
  );
};

// Export both components for backward compatibility
export default CommonDatePicker;
export { CommonDateTimePicker };
